import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { RfiFeedbackPanel } from './RfiFeedbackPanel';
import { RfiReadinessPanel } from './RfiReadinessPanel';
import { Code, ConnectError } from '@connectrpc/connect';
import { Skeleton, SpecraBadge, SpecraButton, SpecraFormField, SpecraInput, SpecraSelect } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Plus, SearchX, Trash2, Unlink } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { LifecycleNodeKind, LinkedChips } from '../../components/LinkedChips';
import { PageBody } from '../../components/layout';
import { parseDateInput, timestampToDateInput } from '../../lib/localCalendarDate';
import { DocumentsPanel } from '../Capture/DocumentsPanel';
import { useCaptureProjects } from '../Capture/hooks';
import { EmptyState, ErrorState } from '../Delivery/_shared/components/PageStates';
import { useCreateRfiResponseProject, useLinkRfiResponseToCapture, useLinkRfiResponseToOpportunity, useRfiResponseWorkspace, useUnlinkRfiResponseFromCapture, useUnlinkRfiResponseFromOpportunity, useUpdateRfiResponseProject } from './hooks';
import { MorgansNoticePicker } from './MorgansNoticePicker';
import { RfiAuthoringPanel } from './RfiAuthoringPanel';
import { RfiOutlinePanel } from './RfiOutlinePanel';
import { RfiPackagePanel } from './RfiPackagePanel';
import { RfiQuestionsPanel } from './RfiQuestionsPanel';
import { RfiSourceVersionsPanel } from './RfiSourceVersionsPanel';
import { RfiSubmissionPanel } from './RfiSubmissionPanel';
import { useMorgansNotice } from './useMorgansNotice';
import { TeamPanel } from './workspace/RfiTeamPanel';
import { useRfiWorkspaceHome } from './workspace/useRfiWorkspaceHome';
const STATUS_OPTIONS = [
    {
        value: 'draft',
        label: 'Draft'
    },
    {
        value: 'in_progress',
        label: 'In progress'
    },
    {
        value: 'submitted',
        label: 'Submitted'
    },
    {
        value: 'closed',
        label: 'Closed'
    }
];
const SECTION_STATUS_OPTIONS = [
    {
        value: 'open',
        label: 'Open'
    },
    {
        value: 'in_progress',
        label: 'In progress'
    },
    {
        value: 'done',
        label: 'Done'
    }
];
export function RfiResponseWorkspacePage() {
    const { id = '' } = useParams();
    return id === 'new' ? /*#__PURE__*/ _jsx(NewRfiResponseForm, {}) : /*#__PURE__*/ _jsx(RfiResponseWorkspace, {
        id: id
    });
}
const TITLE_MAX = 512;
function NewRfiResponseForm() {
    const navigate = useNavigate();
    const create = useCreateRfiResponseProject();
    const [title, setTitle] = useState('');
    const [dueDate, setDueDate] = useState('');
    const [sourceNoticeId, setSourceNoticeId] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    // One key per form, so a retried submit can't create a second RFI Response.
    const [clientRequestId] = useState(()=>crypto.randomUUID());
    const trimmed = title.trim();
    const canSubmit = trimmed.length > 0 && trimmed.length <= TITLE_MAX && !create.isPending;
    const handleSubmit = (e)=>{
        e.preventDefault();
        if (!canSubmit) return;
        setErrorMessage('');
        create.mutate({
            title: trimmed,
            clientRequestId,
            sourceNoticeId: sourceNoticeId || undefined,
            responseDue: parseDateInput(dueDate)
        }, {
            onSuccess: (summary)=>navigate(`/rfi-response/${summary.zouProjectId || summary.rfiResponseProjectId}`, {
                    replace: true
                }),
            onError: (err)=>setErrorMessage(err instanceof Error ? err.message : 'Failed to create RFI Response')
        });
    };
    return /*#__PURE__*/ _jsx(PageBody, {
        children: /*#__PURE__*/ _jsxs("form", {
            className: "space-y-6 px-6 py-4",
            onSubmit: handleSubmit,
            "data-testid": Testids.rfiResponse.newForm,
            children: [
                /*#__PURE__*/ _jsxs("header", {
                    className: "space-y-2",
                    children: [
                        /*#__PURE__*/ _jsx("h1", {
                            className: "text-2xl font-semibold",
                            children: "New RFI Response"
                        }),
                        /*#__PURE__*/ _jsx("p", {
                            className: "text-sm text-muted-foreground",
                            children: "Start a response on its own. You can link pursuits and opportunities after it's created."
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs("section", {
                    className: "grid max-w-3xl gap-4 rounded-lg border p-4",
                    children: [
                        /*#__PURE__*/ _jsx(SpecraFormField, {
                            label: "Title",
                            htmlFor: "rfi-new-title",
                            children: /*#__PURE__*/ _jsx(SpecraInput, {
                                id: "rfi-new-title",
                                value: title,
                                maxLength: TITLE_MAX,
                                required: true,
                                onChange: (e)=>setTitle(e.target.value),
                                "data-testid": Testids.rfiResponse.titleInput
                            })
                        }),
                        /*#__PURE__*/ _jsx(SpecraFormField, {
                            label: "Response due (optional)",
                            htmlFor: "rfi-new-due",
                            children: /*#__PURE__*/ _jsx(SpecraInput, {
                                id: "rfi-new-due",
                                type: "date",
                                value: dueDate,
                                onChange: (e)=>setDueDate(e.target.value),
                                "data-testid": Testids.rfiResponse.dueDateInput
                            })
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-1",
                            children: [
                                /*#__PURE__*/ _jsx("label", {
                                    className: "text-sm font-medium",
                                    children: "Source notice (optional)"
                                }),
                                sourceNoticeId ? /*#__PURE__*/ _jsxs("div", {
                                    className: "flex flex-wrap items-center gap-2",
                                    children: [
                                        /*#__PURE__*/ _jsx(SpecraBadge, {
                                            variant: "info",
                                            children: sourceNoticeId
                                        }),
                                        /*#__PURE__*/ _jsx(SpecraButton, {
                                            size: "sm",
                                            variant: "ghost",
                                            type: "button",
                                            onClick: ()=>setSourceNoticeId(''),
                                            "data-testid": Testids.rfiResponse.sourceNoticeClear,
                                            children: "Clear"
                                        })
                                    ]
                                }) : /*#__PURE__*/ _jsx(MorgansNoticePicker, {
                                    onSelect: (n)=>setSourceNoticeId(n.noticeId),
                                    searchTestId: Testids.rfiResponse.sourceNoticeSearch,
                                    optionTestId: Testids.rfiResponse.sourceNoticeOption,
                                    placeholder: "Search a source Notice…",
                                    ariaLabel: "Search source notice"
                                })
                            ]
                        }),
                        errorMessage ? /*#__PURE__*/ _jsx("p", {
                            role: "alert",
                            className: "text-sm text-destructive",
                            children: errorMessage
                        }) : null,
                        /*#__PURE__*/ _jsx("div", {
                            children: /*#__PURE__*/ _jsx(SpecraButton, {
                                type: "submit",
                                disabled: !canSubmit,
                                "data-testid": Testids.rfiResponse.newSubmitButton,
                                children: "Create RFI Response"
                            })
                        })
                    ]
                })
            ]
        })
    });
}
function RfiResponseWorkspace({ id }) {
    const { data, isLoading, isError, error } = useRfiResponseWorkspace(id);
    const update = useUpdateRfiResponseProject();
    const linkPursuit = useLinkRfiResponseToCapture();
    const unlinkPursuit = useUnlinkRfiResponseFromCapture();
    const linkOpportunity = useLinkRfiResponseToOpportunity();
    const unlinkOpportunity = useUnlinkRfiResponseFromOpportunity();
    const { data: captures = [] } = useCaptureProjects();
    const summary = data?.summary;
    // WRT-06/16: the package dialog preselects the RFI's requested format.
    const { data: home } = useRfiWorkspaceHome(summary?.rfiResponseProjectId ?? '');
    const [title, setTitle] = useState('');
    const [status, setStatus] = useState('draft');
    const [dueDate, setDueDate] = useState('');
    const [sourceNoticeId, setSourceNoticeId] = useState('');
    const [notes, setNotes] = useState('');
    const [sections, setSections] = useState([]);
    const [linkCaptureId, setLinkCaptureId] = useState('');
    useEffect(()=>{
        if (!summary) return;
        setTitle(summary.title);
        setStatus(summary.status || 'draft');
        setDueDate(timestampToDateInput(summary.responseDue));
        setSourceNoticeId(summary.sourceNoticeId ?? '');
        setNotes(summary.notes ?? '');
        setSections(summary.sections.map((s)=>({
                title: s.title,
                status: s.status || 'open'
            })));
    }, [
        summary
    ]);
    const linkedCaptureIds = useMemo(()=>new Set((data?.linkedPursuits ?? []).map((l)=>l.captureProjectId)), [
        data?.linkedPursuits
    ]);
    const linkableCaptures = useMemo(()=>captures.filter((c)=>!linkedCaptureIds.has(c.captureProjectId)), [
        captures,
        linkedCaptureIds
    ]);
    const linkedOpportunityIds = useMemo(()=>new Set((data?.linkedOpportunities ?? []).map((l)=>l.noticeId)), [
        data?.linkedOpportunities
    ]);
    if (isLoading) {
        return /*#__PURE__*/ _jsx(PageBody, {
            children: /*#__PURE__*/ _jsxs("div", {
                className: "space-y-4 px-6 py-4",
                "data-testid": Testids.deliveryShared.loadingSkeleton,
                children: [
                    /*#__PURE__*/ _jsx(Skeleton, {
                        className: "h-8 w-1/3"
                    }),
                    /*#__PURE__*/ _jsx(Skeleton, {
                        className: "h-40"
                    })
                ]
            })
        });
    }
    if (isError || !summary) {
        const notFound = error instanceof ConnectError && error.code === Code.NotFound;
        return /*#__PURE__*/ _jsx(PageBody, {
            children: /*#__PURE__*/ _jsx("div", {
                className: "px-6 py-4",
                children: notFound || !error ? /*#__PURE__*/ _jsx(EmptyState, {
                    icon: SearchX,
                    title: "RFI Response not found",
                    description: "It may have been deleted, or you may not have access to it."
                }) : /*#__PURE__*/ _jsx(ErrorState, {
                    title: "RFI Response workspace unavailable",
                    error: error
                })
            })
        });
    }
    const zouProjectId = summary.zouProjectId;
    const pangaeaId = summary.rfiResponseProjectId;
    const handleSave = ()=>{
        update.mutate({
            rfiResponseProjectId: pangaeaId,
            title,
            status,
            sourceNoticeIdV2: sourceNoticeId,
            responseDue: parseDateInput(dueDate),
            notes,
            sections
        });
    };
    const persistSourceNotice = (noticeId)=>{
        setSourceNoticeId(noticeId);
        update.mutate({
            rfiResponseProjectId: pangaeaId,
            sourceNoticeIdV2: noticeId
        });
    };
    const addSection = ()=>{
        setSections((prev)=>[
                ...prev,
                {
                    title: '',
                    status: 'open'
                }
            ]);
    };
    const updateSection = (index, patch)=>{
        setSections((prev)=>prev.map((s, i)=>i === index ? {
                    ...s,
                    ...patch
                } : s));
    };
    const removeSection = (index)=>{
        setSections((prev)=>prev.filter((_, i)=>i !== index));
    };
    const handleLinkPursuit = ()=>{
        if (!linkCaptureId) return;
        linkPursuit.mutate({
            captureProjectId: linkCaptureId,
            rfiResponseProjectId: pangaeaId
        }, {
            onSuccess: ()=>setLinkCaptureId('')
        });
    };
    return /*#__PURE__*/ _jsx(PageBody, {
        children: /*#__PURE__*/ _jsxs("div", {
            className: "space-y-6 px-6 py-4",
            "data-testid": Testids.rfiResponse.workspaceRoot,
            "data-rfi-response-id": pangaeaId,
            children: [
                /*#__PURE__*/ _jsxs("header", {
                    className: "space-y-2",
                    children: [
                        /*#__PURE__*/ _jsx("h1", {
                            className: "text-2xl font-semibold",
                            children: "RFI Response"
                        }),
                        /*#__PURE__*/ _jsxs("p", {
                            className: "text-sm text-muted-foreground",
                            children: [
                                "Collaborative workspace • ",
                                /*#__PURE__*/ _jsx(SpecraBadge, {
                                    variant: "neutral",
                                    children: status
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx(LinkedChips, {
                            className: "mt-2",
                            kind: LifecycleNodeKind.RFI_RESPONSE,
                            id: zouProjectId || pangaeaId
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs("section", {
                    className: "grid max-w-3xl gap-4 rounded-lg border p-4",
                    children: [
                        /*#__PURE__*/ _jsx(SpecraFormField, {
                            label: "Title",
                            htmlFor: "rfi-title",
                            children: /*#__PURE__*/ _jsx(SpecraInput, {
                                id: "rfi-title",
                                value: title,
                                onChange: (e)=>setTitle(e.target.value),
                                "data-testid": Testids.rfiResponse.titleInput
                            })
                        }),
                        /*#__PURE__*/ _jsx(SpecraFormField, {
                            label: "Status",
                            htmlFor: "rfi-status",
                            children: /*#__PURE__*/ _jsx(SpecraSelect, {
                                id: "rfi-status",
                                value: status,
                                onChange: (e)=>setStatus(e.target.value),
                                "data-testid": Testids.rfiResponse.statusSelect,
                                children: STATUS_OPTIONS.map((o)=>/*#__PURE__*/ _jsx("option", {
                                        value: o.value,
                                        children: o.label
                                    }, o.value))
                            })
                        }),
                        /*#__PURE__*/ _jsx(SpecraFormField, {
                            label: "Response due",
                            htmlFor: "rfi-due",
                            children: /*#__PURE__*/ _jsx(SpecraInput, {
                                id: "rfi-due",
                                type: "date",
                                value: dueDate,
                                onChange: (e)=>setDueDate(e.target.value),
                                "data-testid": Testids.rfiResponse.dueDateInput
                            })
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-1",
                            children: [
                                /*#__PURE__*/ _jsx("label", {
                                    className: "text-sm font-medium",
                                    children: "Source notice"
                                }),
                                /*#__PURE__*/ _jsx("p", {
                                    className: "text-xs text-muted-foreground",
                                    children: "The 0..1 market Notice this workspace answers. Separate from linked opportunities."
                                }),
                                sourceNoticeId ? /*#__PURE__*/ _jsx(SourceNoticeChip, {
                                    noticeId: sourceNoticeId,
                                    onClear: ()=>persistSourceNotice(''),
                                    disabled: update.isPending
                                }) : /*#__PURE__*/ _jsx(MorgansNoticePicker, {
                                    onSelect: (n)=>persistSourceNotice(n.noticeId),
                                    searchTestId: Testids.rfiResponse.sourceNoticeSearch,
                                    optionTestId: Testids.rfiResponse.sourceNoticeOption,
                                    placeholder: "Search a source Notice…",
                                    ariaLabel: "Search source notice"
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx(SpecraFormField, {
                            label: "Notes",
                            htmlFor: "rfi-notes",
                            children: /*#__PURE__*/ _jsx(SpecraInput, {
                                id: "rfi-notes",
                                value: notes,
                                onChange: (e)=>setNotes(e.target.value)
                            })
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            children: /*#__PURE__*/ _jsx(SpecraButton, {
                                onClick: handleSave,
                                disabled: update.isPending,
                                children: "Save"
                            })
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(RfiReadinessPanel, {
                    rfiResponseProjectId: pangaeaId
                }, `readiness-${pangaeaId}`),
                /*#__PURE__*/ _jsx(RfiSourceVersionsPanel, {
                    rfiResponseProjectId: pangaeaId
                }, `source-${pangaeaId}`),
                /*#__PURE__*/ _jsx(RfiQuestionsPanel, {
                    rfiResponseProjectId: pangaeaId
                }, pangaeaId),
                /*#__PURE__*/ _jsx(RfiOutlinePanel, {
                    rfiResponseProjectId: pangaeaId
                }),
                /*#__PURE__*/ _jsx(RfiPackagePanel, {
                    preferredFormat: packageFormat(home?.preferredPackageFormat),
                    rfiResponseProjectId: pangaeaId,
                    submissionFormat: home?.submissionFormat,
                    zouProjectId: zouProjectId
                }),
                /*#__PURE__*/ _jsx(RfiSubmissionPanel, {
                    rfiResponseProjectId: pangaeaId,
                    zouProjectId: zouProjectId
                }),
                /*#__PURE__*/ _jsx(RfiFeedbackPanel, {
                    rfiResponseProjectId: pangaeaId,
                    zouProjectId: zouProjectId
                }, `feedback-${pangaeaId}`),
                /*#__PURE__*/ _jsx(RfiAuthoringPanel, {
                    rfiResponseProjectId: pangaeaId,
                    zouProjectId: zouProjectId
                }),
                /*#__PURE__*/ _jsxs("section", {
                    className: "max-w-3xl space-y-2",
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: "flex items-center justify-between gap-2",
                            children: [
                                /*#__PURE__*/ _jsx("h2", {
                                    className: "text-lg font-medium",
                                    children: "Legacy section checklist"
                                }),
                                /*#__PURE__*/ _jsx(SpecraButton, {
                                    size: "sm",
                                    variant: "secondary",
                                    onClick: addSection,
                                    iconLeft: /*#__PURE__*/ _jsx(Plus, {
                                        className: "h-4 w-4"
                                    }),
                                    "data-testid": Testids.rfiResponse.addSectionButton,
                                    children: "Add section"
                                })
                            ]
                        }),
                        sections.length === 0 ? /*#__PURE__*/ _jsx("p", {
                            className: "text-sm text-muted-foreground",
                            children: "No sections yet."
                        }) : /*#__PURE__*/ _jsx("ul", {
                            className: "divide-y rounded-md border",
                            children: sections.map((s, i)=>/*#__PURE__*/ _jsxs("li", {
                                    className: "flex flex-wrap items-center gap-2 px-3 py-2",
                                    "data-testid": Testids.rfiResponse.sectionRow,
                                    children: [
                                        /*#__PURE__*/ _jsx(SpecraInput, {
                                            className: "min-w-[12rem] flex-1",
                                            value: s.title,
                                            onChange: (e)=>updateSection(i, {
                                                    title: e.target.value
                                                }),
                                            placeholder: "Section title",
                                            "data-testid": Testids.rfiResponse.sectionTitleInput
                                        }),
                                        /*#__PURE__*/ _jsx(SpecraSelect, {
                                            className: "w-36",
                                            value: s.status || 'open',
                                            onChange: (e)=>updateSection(i, {
                                                    status: e.target.value
                                                }),
                                            "data-testid": Testids.rfiResponse.sectionStatusSelect,
                                            children: SECTION_STATUS_OPTIONS.map((o)=>/*#__PURE__*/ _jsx("option", {
                                                    value: o.value,
                                                    children: o.label
                                                }, o.value))
                                        }),
                                        /*#__PURE__*/ _jsx(SpecraButton, {
                                            size: "sm",
                                            variant: "ghost",
                                            onClick: ()=>removeSection(i),
                                            "aria-label": "Remove section",
                                            "data-testid": Testids.rfiResponse.removeSectionButton,
                                            iconLeft: /*#__PURE__*/ _jsx(Trash2, {
                                                className: "h-4 w-4"
                                            })
                                        })
                                    ]
                                }, `section-${i}`))
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs("section", {
                    className: "max-w-3xl space-y-2",
                    children: [
                        /*#__PURE__*/ _jsx("h2", {
                            className: "text-lg font-medium",
                            children: "Linked pursuits"
                        }),
                        data.linkedPursuits.length === 0 ? /*#__PURE__*/ _jsx("p", {
                            className: "text-sm text-muted-foreground",
                            children: "This response isn't linked to any pursuit. Link one or more below."
                        }) : /*#__PURE__*/ _jsx("ul", {
                            className: "flex flex-col gap-2",
                            children: data.linkedPursuits.map((l)=>/*#__PURE__*/ _jsxs("li", {
                                    className: "flex items-center gap-2",
                                    children: [
                                        /*#__PURE__*/ _jsx(Link, {
                                            to: `/capture/${l.captureProjectId}`,
                                            className: "no-underline",
                                            "data-testid": Testids.rfiResponse.openLink,
                                            children: /*#__PURE__*/ _jsx(SpecraBadge, {
                                                variant: "info",
                                                children: l.captureName || l.captureProjectId
                                            })
                                        }),
                                        /*#__PURE__*/ _jsx(SpecraButton, {
                                            size: "sm",
                                            variant: "ghost",
                                            onClick: ()=>unlinkPursuit.mutate({
                                                    captureProjectId: l.captureProjectId,
                                                    rfiResponseProjectId: pangaeaId
                                                }),
                                            disabled: unlinkPursuit.isPending,
                                            "data-testid": Testids.rfiResponse.unlinkPursuitButton,
                                            iconLeft: /*#__PURE__*/ _jsx(Unlink, {
                                                className: "h-4 w-4"
                                            }),
                                            children: "Unlink"
                                        })
                                    ]
                                }, l.linkId))
                        }),
                        linkableCaptures.length > 0 && /*#__PURE__*/ _jsxs("div", {
                            className: "flex flex-wrap items-end gap-2 pt-2",
                            children: [
                                /*#__PURE__*/ _jsx(SpecraFormField, {
                                    label: "Link existing pursuit",
                                    htmlFor: "rfi-link-pursuit",
                                    children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                                        id: "rfi-link-pursuit",
                                        value: linkCaptureId,
                                        onChange: (e)=>setLinkCaptureId(e.target.value),
                                        "data-testid": Testids.rfiResponse.linkPursuitSelect,
                                        children: [
                                            /*#__PURE__*/ _jsx("option", {
                                                value: "",
                                                children: "Select pursuit…"
                                            }),
                                            linkableCaptures.map((c)=>/*#__PURE__*/ _jsx("option", {
                                                    value: c.captureProjectId,
                                                    children: c.name
                                                }, c.captureProjectId))
                                        ]
                                    })
                                }),
                                /*#__PURE__*/ _jsx(SpecraButton, {
                                    size: "sm",
                                    variant: "secondary",
                                    onClick: handleLinkPursuit,
                                    disabled: !linkCaptureId || linkPursuit.isPending,
                                    "data-testid": Testids.rfiResponse.linkPursuitButton,
                                    children: "Link"
                                })
                            ]
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs("section", {
                    className: "max-w-3xl space-y-2",
                    children: [
                        /*#__PURE__*/ _jsx("h2", {
                            className: "text-lg font-medium",
                            children: "Linked opportunities"
                        }),
                        /*#__PURE__*/ _jsx("p", {
                            className: "text-xs text-muted-foreground",
                            children: "M:N chase-layer links. A gov question can span more than one opportunity."
                        }),
                        (data.linkedOpportunities ?? []).length === 0 ? /*#__PURE__*/ _jsx("p", {
                            className: "text-sm text-muted-foreground",
                            children: "No opportunities linked yet."
                        }) : /*#__PURE__*/ _jsx("ul", {
                            className: "flex flex-col gap-2",
                            children: (data.linkedOpportunities ?? []).map((l)=>/*#__PURE__*/ _jsxs("li", {
                                    className: "flex items-center gap-2",
                                    children: [
                                        /*#__PURE__*/ _jsx(LinkedOpportunityChip, {
                                            noticeId: l.noticeId
                                        }),
                                        /*#__PURE__*/ _jsx(SpecraButton, {
                                            size: "sm",
                                            variant: "ghost",
                                            onClick: ()=>unlinkOpportunity.mutate({
                                                    noticeId: l.noticeId,
                                                    rfiResponseProjectId: pangaeaId
                                                }),
                                            disabled: unlinkOpportunity.isPending,
                                            "data-testid": Testids.rfiResponse.unlinkOpportunityButton,
                                            iconLeft: /*#__PURE__*/ _jsx(Unlink, {
                                                className: "h-4 w-4"
                                            }),
                                            children: "Unlink"
                                        })
                                    ]
                                }, l.linkId))
                        }),
                        /*#__PURE__*/ _jsx(MorgansNoticePicker, {
                            excludedNoticeIds: linkedOpportunityIds,
                            onSelect: (n)=>linkOpportunity.mutate({
                                    noticeId: n.noticeId,
                                    rfiResponseProjectId: pangaeaId
                                }),
                            searchTestId: Testids.rfiResponse.linkOpportunitySearch,
                            optionTestId: Testids.rfiResponse.linkOpportunityOption,
                            placeholder: "Search an opportunity to link…",
                            ariaLabel: "Search opportunity to link"
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs("section", {
                    className: "max-w-3xl space-y-2",
                    children: [
                        /*#__PURE__*/ _jsx("h2", {
                            className: "text-lg font-medium",
                            children: "Documents"
                        }),
                        /*#__PURE__*/ _jsx(DocumentsPanel, {
                            entityType: "rfi_response_project",
                            entityId: pangaeaId
                        })
                    ]
                }),
                zouProjectId ? /*#__PURE__*/ _jsx(TeamPanel, {
                    zouProjectId: zouProjectId
                }) : null
            ]
        })
    });
}
/** WRT-06 `preferred_package_format` as the dialog's format ("" = no preference). */ function packageFormat(value) {
    return value === 'pdf' || value === 'docx' ? value : undefined;
}
function SourceNoticeChip({ noticeId, onClear, disabled }) {
    const { data, isError, isLoading } = useMorgansNotice(noticeId);
    const title = data?.title;
    const unresolved = isError || !isLoading && !title;
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex flex-wrap items-center gap-2",
        children: [
            /*#__PURE__*/ _jsx(Link, {
                to: "/opportunities",
                className: "no-underline",
                children: /*#__PURE__*/ _jsx(SpecraBadge, {
                    variant: unresolved ? 'warning' : 'info',
                    "data-testid": unresolved ? Testids.rfiResponse.sourceNoticeUnresolved : Testids.rfiResponse.sourceNoticeChip,
                    children: unresolved ? `Unresolved notice ${noticeId}` : `${title} · ${noticeId}`
                })
            }),
            /*#__PURE__*/ _jsx(SpecraButton, {
                size: "sm",
                variant: "ghost",
                onClick: onClear,
                disabled: disabled,
                "data-testid": Testids.rfiResponse.sourceNoticeClear,
                children: "Clear"
            })
        ]
    });
}
function LinkedOpportunityChip({ noticeId }) {
    const { data, isError } = useMorgansNotice(noticeId);
    const label = data?.title ? `${data.title} · ${noticeId}` : noticeId;
    return /*#__PURE__*/ _jsx(Link, {
        to: "/opportunities",
        className: "no-underline",
        children: /*#__PURE__*/ _jsx(SpecraBadge, {
            variant: isError ? 'warning' : 'info',
            "data-testid": Testids.rfiResponse.linkedOpportunityChip,
            children: isError ? `Unresolved opportunity ${noticeId}` : label
        })
    });
}
